import { supabase } from "@/lib/supabaseClient";
import type { Reservation, ReservationStatus } from "@/lib/supabase/types";

/**
 * Reservations service. Create goes through the SECURITY DEFINER RPC
 * so we get guest upsert + confirmation code generation + atomic
 * overlap protection. Reads and updates go through the standard
 * Supabase client — RLS scopes everything to the caller's org.
 */

/** View model — reservation row joined with guest name + room number
 *  for list rendering. */
export interface ReservationListRow extends Reservation {
  guestName: string;
  guestNameAr: string | null;
  guestPhone: string | null;
  roomNumber: string | null;
}

export interface ListFilters {
  propertyId: string;
  status?: ReservationStatus | "all";
  search?: string;               // confirmation code OR guest name
  from?: string;                 // check_in_date >=
  to?: string;                   // check_in_date <=
  limit?: number;
}

export async function listReservations(filters: ListFilters): Promise<ReservationListRow[]> {
  let q = supabase
    .from("reservations")
    .select("*, primary_guest:guests!reservations_primary_guest_id_fkey(full_name_en, full_name_ar, phone), room:rooms(number)")
    .eq("property_id", filters.propertyId);

  if (filters.status && filters.status !== "all") {
    q = q.eq("status", filters.status);
  }
  if (filters.from) q = q.gte("check_in_date", filters.from);
  if (filters.to)   q = q.lte("check_in_date", filters.to);

  // Most hotels want the newest first — arrivals-today surface above
  // historical stays.
  q = q.order("check_in_date", { ascending: false }).limit(filters.limit ?? 200);

  const { data, error } = await q;
  if (error) throw error;

  const rows: ReservationListRow[] = (data ?? []).map((row: any) => ({
    ...(row as Reservation),
    guestName:   row.primary_guest?.full_name_en ?? "—",
    guestNameAr: row.primary_guest?.full_name_ar ?? null,
    guestPhone:  row.primary_guest?.phone ?? null,
    roomNumber:  row.room?.number ?? null,
  }));

  // Client-side search (confirmation code or guest name). Push down
  // to the DB later when the list grows past a few hundred rows.
  if (filters.search && filters.search.trim()) {
    const needle = filters.search.trim().toLowerCase();
    return rows.filter((r) =>
      r.confirmation_code.toLowerCase().includes(needle) ||
      r.guestName.toLowerCase().includes(needle) ||
      (r.guestPhone ?? "").toLowerCase().includes(needle),
    );
  }
  return rows;
}

// =================================================================
// Create reservation — goes through the RPC for atomic guest upsert
// + confirmation code + overlap guard.
// =================================================================

export interface CreateReservationInput {
  org_id: string;
  property_id: string;
  room_id: string | null;
  guest_full_name_en: string;
  guest_full_name_ar?: string | null;
  guest_email?: string | null;
  guest_phone?: string | null;
  guest_nationality?: string | null;
  check_in_date: string;         // YYYY-MM-DD
  check_out_date: string;        // YYYY-MM-DD
  adults?: number;
  children?: number;
  source?: string;
  notes?: string | null;
  total_amount?: number | null;
}

export interface CreateReservationResult {
  reservation_id: string;
  confirmation_code: string;
  guest_id: string;
  status: ReservationStatus;
}

export async function createReservation(input: CreateReservationInput): Promise<CreateReservationResult> {
  const { data, error } = await supabase.rpc("diyafa_create_reservation", {
    p_org_id:             input.org_id,
    p_property_id:        input.property_id,
    p_room_id:            input.room_id,
    p_guest_full_name_en: input.guest_full_name_en,
    p_check_in_date:      input.check_in_date,
    p_check_out_date:     input.check_out_date,
    p_guest_full_name_ar: input.guest_full_name_ar ?? null,
    p_guest_email:        input.guest_email ?? null,
    p_guest_phone:        input.guest_phone ?? null,
    p_guest_nationality:  input.guest_nationality ?? null,
    p_adults:             input.adults ?? 1,
    p_children:           input.children ?? 0,
    p_source:             input.source ?? "direct",
    p_notes:              input.notes ?? null,
    p_total_amount:       input.total_amount ?? null,
  });
  if (error) throw error;
  const result = data as CreateReservationResult;
  // Sprint 7 · Rasad sync on booking (pre-check-in).
  const { fireAndForgetReservationSync } = await import("@/lib/supabase/rasad");
  fireAndForgetReservationSync(result.reservation_id, "booking");
  return result;
}

// v1.18.0 · Edit Reservation
export interface UpdateReservationInput {
  reservation_id: string;
  check_in_date?:  string;
  check_out_date?: string;
  room_id?:        string | null;  // pass null to clear
  adults?:         number;
  children?:       number;
  total_amount?:   number | null;
  source?:         string;
  notes?:          string;
}

export interface UpdateReservationResult {
  reservation_id: string;
  confirmation_code: string;
  rasad_reference: string | null;
  status: ReservationStatus;
  room_id: string | null;
  check_in_date: string;
  check_out_date: string;
}

export async function updateReservation(input: UpdateReservationInput): Promise<UpdateReservationResult> {
  const clearRoom = input.room_id === null;
  const { data, error } = await supabase.rpc("diyafa_update_reservation", {
    p_reservation_id: input.reservation_id,
    p_check_in_date:  input.check_in_date  ?? null,
    p_check_out_date: input.check_out_date ?? null,
    p_room_id:        clearRoom ? null : input.room_id ?? null,
    p_adults:         input.adults ?? null,
    p_children:       input.children ?? null,
    p_total_amount:   input.total_amount ?? null,
    p_source:         input.source ?? null,
    p_notes:          input.notes ?? null,
    p_clear_room:     clearRoom,
  });
  if (error) throw error;
  // Report the edit to Rasad (HTL_BOOKING_UPDATE); each edit is its own event.
  const { fireAndForgetReservationSync } = await import("@/lib/supabase/rasad");
  fireAndForgetReservationSync(input.reservation_id, "booking_update");
  return data as UpdateReservationResult;
}

export async function cancelReservation(reservationId: string, reason?: string): Promise<void> {
  const { error } = await supabase.rpc("diyafa_cancel_reservation", {
    p_reservation_id: reservationId,
    p_reason: reason ?? null,
  });
  if (error) throw error;
  // Only a cancellation that actually happened is reported (HTL_CANCEL). The row persists for audit,
  // so the edge function can still read the booking and guest.
  const { fireAndForgetReservationSync } = await import("@/lib/supabase/rasad");
  fireAndForgetReservationSync(reservationId, "cancellation");
}

// =================================================================
// CheckIn / CheckOut
// =================================================================

export interface ServiceLineItem {
  code:   string;           // 'early_checkin' | 'late_checkout' | 'breakfast' | 'extra_bed' | 'airport_xfr' | 'custom'
  label:  string;           // human-readable
  qty:    number;
  amount: number;           // OMR, per unit
  meta?:  Record<string, unknown>;
}

export interface CheckinMeta {
  arrival_time?:   string;     // 'HH:MM'
  departure_time?: string;
  meal_plan?:      "room_only" | "bb" | "hb" | "fb";
  early_checkin_fee?: number;  // OMR (already summed into services)
  late_checkout_fee?: number;
  // v1.22.11 — additional-guest roster + minors block so the Rasad
  // edge fn can surface the full party (not just the primary guest).
  additional_guest_ids?: string[];
  minors?: Array<{
    label: string;
    skip_reason: string;
    age: number | null;
    name: string | null;
  }>;
}

export interface CheckInInput {
  reservation_id: string;
  room_id?: string | null;
  deposit_amount?: number | null;
  deposit_method?: string;
  notes?: string | null;
  /** Sprint 15b wizard additions — persisted on the reservation row
   *  before the check-in RPC runs. Optional for back-compat with the
   *  old simple form. */
  services?: ServiceLineItem[];
  checkin_meta?: CheckinMeta;
}

export async function checkInReservation(input: CheckInInput): Promise<void> {
  // Persist wizard Step-3 state on the reservation row first, so if
  // the RPC fails we still have the draft saved.
  if (input.services || input.checkin_meta) {
    const patch: Record<string, unknown> = {};
    if (input.services)     patch.services     = input.services;
    if (input.checkin_meta) patch.checkin_meta = input.checkin_meta;
    const { error: patchErr } = await supabase.from("reservations").update(patch).eq("id", input.reservation_id);
    if (patchErr) throw patchErr;
  }

  const { error } = await supabase.rpc("diyafa_check_in", {
    p_reservation_id: input.reservation_id,
    p_room_id:        input.room_id ?? null,
    p_deposit_amount: input.deposit_amount ?? null,
    p_deposit_method: input.deposit_method ?? "cash",
    p_notes:          input.notes ?? null,
  });
  if (error) throw error;

  // Persist service line items as transaction rows (creates the
  // "invoice" — see Q7 decision: real transactions). The deposit row
  // is already created by the RPC; we only add the service rows here.
  if (input.services && input.services.length > 0) {
    const { data: res } = await supabase.from("reservations").select("org_id, property_id, primary_guest_id").eq("id", input.reservation_id).maybeSingle();
    if (res) {
      const rows = input.services.map((s) => ({
        org_id: res.org_id, property_id: res.property_id, reservation_id: input.reservation_id,
        guest_id: res.primary_guest_id, type: "extras" as const,
        description: s.label, amount: s.amount * s.qty, currency: "OMR", method: null, reference: s.code,
      }));
      await supabase.from("transactions").insert(rows);
    }
  }

  // Sprint 7 · real Rasad sync (fire-and-forget — UI reads rasad_status
  // from Realtime on the reservations row).
  const { fireAndForgetReservationSync } = await import("@/lib/supabase/rasad");
  fireAndForgetReservationSync(input.reservation_id, "check_in");
}

export interface CheckOutInput {
  reservation_id: string;
  payment_amount?: number | null;
  payment_method?: string;           // 'cash' | 'card' | 'transfer'
  extra_charges?: Array<{ desc: string; amount: number }>;
  notes?: string | null;
}

export interface CheckOutResult {
  reservation_id: string;
  total_charged: number;
  total_paid: number;
  balance: number;   // positive = guest still owes us
}

export async function checkOutReservation(input: CheckOutInput): Promise<CheckOutResult> {
  const { data, error } = await supabase.rpc("diyafa_check_out", {
    p_reservation_id: input.reservation_id,
    p_payment_amount: input.payment_amount ?? null,
    p_payment_method: input.payment_method ?? "cash",
    p_extra_charges:  input.extra_charges ?? null,
    p_notes:          input.notes ?? null,
  });
  if (error) throw error;
  // Sprint 7 · real Rasad sync (check-out event).
  const { fireAndForgetReservationSync } = await import("@/lib/supabase/rasad");
  fireAndForgetReservationSync(input.reservation_id, "check_out");
  return {
    reservation_id: (data as any).reservation_id,
    total_charged: Number((data as any).total_charged) || 0,
    total_paid: Number((data as any).total_paid) || 0,
    balance: Number((data as any).balance) || 0,
  };
}

/** Get a reservation with full guest + room context for the checkin/
 *  checkout panels. */
export async function getReservationWithContext(id: string): Promise<ReservationListRow | null> {
  const { data, error } = await supabase
    .from("reservations")
    .select("*, primary_guest:guests!reservations_primary_guest_id_fkey(full_name_en, full_name_ar, phone), room:rooms(number)")
    .eq("id", id)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return {
    ...(data as Reservation),
    guestName:   (data as any).primary_guest?.full_name_en ?? "—",
    guestNameAr: (data as any).primary_guest?.full_name_ar ?? null,
    guestPhone:  (data as any).primary_guest?.phone ?? null,
    roomNumber:  (data as any).room?.number ?? null,
  };
}

// =================================================================
// v1.22.12 — Room Change (Diyafa PMS core action, Rasad notification)
// =================================================================

export type RoomChangeReason =
  | "upgrade"
  | "downgrade"
  | "guest_request"
  | "plumbing_issue"
  | "housekeeping_delay"
  | "complaint_recovery"
  | "overbooking"
  | "maintenance"
  | "other";

export interface ChangeRoomInput {
  reservation_id:  string;
  to_room_id:      string;
  reason:          RoomChangeReason;
  notes?:          string | null;
  rate_diff?:      number;          // OMR (positive for upgrade, negative for downgrade)
  charge_upgrade?: boolean;         // if true and rate_diff > 0 → post as 'extras' transaction
  change_date?:    string;          // YYYY-MM-DD, defaults to today server-side
}

export interface RoomChangeRow {
  id: string;
  reservation_id: string;
  from_room_id: string | null;
  to_room_id: string;
  from_room_type: string | null;
  to_room_type: string | null;
  change_date: string;
  reason: string;
  notes: string | null;
  rate_diff: number;
  charge_upgrade: boolean;
  rasad_event_id: string | null;
  rasad_status: string | null;
  created_at: string;
}

/** Swap the room on an active reservation, flip room statuses, queue
 *  housekeeping on the vacated room, optionally post a rate-diff
 *  transaction, and fire a Rasad `room_change` event. Atomically.
 *
 *  Diyafa owns the business logic (folio, housekeeping). Rasad just
 *  receives the compliance event afterward. */
export async function changeRoom(input: ChangeRoomInput): Promise<RoomChangeRow> {
  const { data, error } = await supabase.rpc("diyafa_change_room", {
    p_reservation_id: input.reservation_id,
    p_to_room_id:     input.to_room_id,
    p_reason:         input.reason,
    p_notes:          input.notes ?? null,
    p_rate_diff:      input.rate_diff ?? 0,
    p_charge_upgrade: !!input.charge_upgrade,
    p_change_date:    input.change_date ?? null,
  });
  if (error) throw error;

  // Fire the Rasad sync (fire-and-forget — same pattern as check-in/out).
  const { fireAndForgetReservationSync } = await import("@/lib/supabase/rasad");
  fireAndForgetReservationSync(input.reservation_id, "room_change");

  return data as RoomChangeRow;
}

// =================================================================
// v1.22.12 — Universal reservation search (mirrors Rasad Booking Lookup)
// =================================================================
// Matches the query against ANY of these fields simultaneously:
//   • reservations.confirmation_code  (BK / HTL / CAR…)
//   • guests.full_name_en / full_name_ar  (surname match → family)
//   • guests.document_number              (passport / ID)
//   • guests.phone / email
//   • rooms.number                        (current room #)
//
// Returns top N matches, ranked by most recent activity. Used by
// BookingLookup card + Cmd+K universal search.

export interface SearchHit {
  id: string;
  confirmation_code: string;
  status: string;
  check_in_date: string;
  check_out_date: string;
  adults: number;
  children: number;
  room_id: string | null;
  room_number: string | null;
  guest_name: string;
  guest_name_ar: string | null;
  guest_phone: string | null;
  guest_nationality: string | null;
  document_number: string | null;
  match_field: "code" | "name" | "passport" | "room" | "phone" | "email" | "other";
}

export async function searchReservations(
  orgId: string,
  query: string,
  limit = 10,
): Promise<SearchHit[]> {
  const q = query.trim();
  if (!q) return [];

  // Fetch in parallel: by code (exact/prefix), by guest attrs, by room #.
  // Supabase doesn't do cross-table OR cleanly; we union client-side.
  const like = `%${q}%`;
  const [byCode, byGuest, byRoom] = await Promise.all([
    supabase.from("reservations")
      .select("id, confirmation_code, status, check_in_date, check_out_date, adults, children, room_id, primary_guest_id, rooms(number), guests!reservations_primary_guest_id_fkey(full_name_en, full_name_ar, phone, nationality, document_number)")
      .eq("org_id", orgId)
      .ilike("confirmation_code", like)
      .order("check_in_date", { ascending: false })
      .limit(limit),
    supabase.from("guests")
      .select("id, full_name_en, full_name_ar, phone, email, nationality, document_number, reservations!reservations_primary_guest_id_fkey(id, confirmation_code, status, check_in_date, check_out_date, adults, children, room_id, rooms(number))")
      .eq("org_id", orgId)
      .or(`full_name_en.ilike.${like},full_name_ar.ilike.${like},phone.ilike.${like},email.ilike.${like},document_number.ilike.${like}`)
      .limit(limit),
    supabase.from("rooms")
      .select("id, number, reservations!reservations_room_id_fkey(id, confirmation_code, status, check_in_date, check_out_date, adults, children, room_id, primary_guest_id, guests!reservations_primary_guest_id_fkey(full_name_en, full_name_ar, phone, nationality, document_number))")
      .ilike("number", like)
      .limit(limit),
  ]);

  const hits = new Map<string, SearchHit>();
  const add = (r: any, guestRow: any, roomNum: string | null, field: SearchHit["match_field"]) => {
    if (!r?.id || hits.has(r.id)) return;
    hits.set(r.id, {
      id: r.id,
      confirmation_code: r.confirmation_code,
      status: r.status,
      check_in_date: r.check_in_date,
      check_out_date: r.check_out_date,
      adults: r.adults ?? 0,
      children: r.children ?? 0,
      room_id: r.room_id ?? null,
      room_number: roomNum,
      guest_name: guestRow?.full_name_en ?? "—",
      guest_name_ar: guestRow?.full_name_ar ?? null,
      guest_phone: guestRow?.phone ?? null,
      guest_nationality: guestRow?.nationality ?? null,
      document_number: guestRow?.document_number ?? null,
      match_field: field,
    });
  };

  (byCode.data ?? []).forEach((r: any) =>
    add(r, (r.guests as any) ?? null, (r.rooms as any)?.number ?? null, "code"));
  (byGuest.data ?? []).forEach((g: any) => {
    const field: SearchHit["match_field"] =
      g.document_number && g.document_number.toLowerCase().includes(q.toLowerCase()) ? "passport"
      : g.phone && g.phone.includes(q) ? "phone"
      : g.email && g.email.toLowerCase().includes(q.toLowerCase()) ? "email"
      : "name";
    (g.reservations ?? []).forEach((r: any) => add(r, g, r.rooms?.number ?? null, field));
  });
  (byRoom.data ?? []).forEach((rm: any) =>
    (rm.reservations ?? []).forEach((r: any) =>
      add(r, r.guests ?? null, rm.number, "room")));

  // Sort by check_in_date desc, active statuses first.
  const rank = (s: string) => s === "checked_in" ? 0 : s === "confirmed" || s === "pending" ? 1 : 2;
  return Array.from(hits.values())
    .sort((a, b) => rank(a.status) - rank(b.status) || b.check_in_date.localeCompare(a.check_in_date))
    .slice(0, limit);
}

/** List every room change recorded for a reservation (audit timeline). */
export async function listRoomChanges(reservationId: string): Promise<RoomChangeRow[]> {
  const { data, error } = await supabase
    .from("reservation_room_changes")
    .select("*")
    .eq("reservation_id", reservationId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data as RoomChangeRow[]) ?? [];
}

/** Running total for a reservation (charges − payments/deposits). */
export async function getReservationBalance(reservationId: string): Promise<{
  charges: number;
  payments: number;
  balance: number;
  transactions: Array<{ id: string; type: string; description: string; amount: number; method: string | null; posted_at: string }>;
}> {
  const { data, error } = await supabase
    .from("transactions")
    .select("id, type, description, amount, method, posted_at")
    .eq("reservation_id", reservationId)
    .order("posted_at", { ascending: true });
  if (error) throw error;
  const charges = (data ?? []).filter((t) => ["room_charge","extras","tax","adjustment"].includes(t.type))
    .reduce((s, t) => s + Number(t.amount), 0);
  const payments = (data ?? []).filter((t) => ["payment","deposit"].includes(t.type))
    .reduce((s, t) => s + Number(t.amount), 0);
  return {
    charges, payments,
    balance: charges - payments,
    transactions: (data ?? []) as any,
  };
}
